'use client';

import cn from '@sindresorhus/class-names';
import {
  ButtonHTMLAttributes,
  DetailedHTMLProps,
  ElementType,
  memo,
  ReactElement,
  ReactNode,
} from 'react';

import {Spinner} from '../Spinner';
import type {
  PolymorphicComponentPropWithRef,
  PolymorphicRef,
} from '../utils/types';
import styles from './Button.module.scss';

type ButtonProps = {
  kind?: 'primary' | 'secondary' | 'outline' | 'inline' | 'inline-black';
  size?: 'xs' | 'sm' | 'md' | 'cube'; // 'lg' | 'xl' are not yet defined
  label?: ReactNode;
  href?: string;
  forwardedRef?: PolymorphicRef<'button'>;
  beforeIcon?: ReactElement;
  afterIcon?: ReactElement;
  isLoading?: boolean;
  disabled?: boolean;
} & DetailedHTMLProps<
  ButtonHTMLAttributes<HTMLButtonElement>,
  HTMLButtonElement
>;

export const Button = memo(
  <C extends ElementType = 'button'>({
    className,
    kind = 'primary',
    size = 'md',
    label,
    beforeIcon,
    afterIcon,
    isLoading,
    disabled,
    href,
    ref,
    ...rest
  }: PolymorphicComponentPropWithRef<C, ButtonProps>) => {
    const Component = href ? 'a' : 'button';

    return (
      <Component
        type={!href ? 'button' : undefined}
        className={cn(className, styles[kind], styles[size], styles.button)}
        disabled={disabled}
        ref={ref}
        href={href}
        {...rest}
      >
        {isLoading && (
          <Spinner
            color={kind === 'primary' ? 'var(--vk-primary)' : '#ffffff'}
          />
        )}

        {beforeIcon && beforeIcon}

        {label && label}

        {afterIcon && afterIcon}
      </Component>
    );
  },
);

Button.displayName = 'Button';
